import { z } from 'zod'
import { SeriesModelLink, SeriesReviewReason } from './series-alignment.js'

const Year = z.coerce.number().int().min(1000).max(9999)
export const SeriesReportQuery = z
  .object({
    fromYear: Year,
    toYear: Year,
    identityIds: z
      .string()
      .max(3699)
      .transform(value => value.split(','))
      .pipe(
        z
          .array(z.guid())
          .min(1)
          .max(100)
          .refine(ids => new Set(ids).size === ids.length, 'Choose distinct identities.')
      )
      .optional(),
  })
  .strict()
  .refine(
    q => q.toYear >= q.fromYear && q.toYear - q.fromYear < 50,
    'Choose an inclusive range of at most 50 years.'
  )
export type SeriesReportSelection = { fromYear: number; toYear: number; identityIds?: string[] }
export const SeriesTrendIdentity = z.object({
  ...SeriesModelLink.shape,
  id: z.guid(),
  label: z.string(),
  retired: z.boolean(),
})
export const SeriesReportIdentitiesQuery = z
  .object({
    cursor: z.string().max(1000).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict()
export const SeriesReportIdentitiesResponse = z.object({
  identities: z.array(SeriesTrendIdentity).max(100),
  nextCursor: z.string().nullable(),
})
export type SeriesReportIdentitiesOutput = z.infer<typeof SeriesReportIdentitiesResponse>
const Count = z.number().int().nonnegative()
const CoveragePart = z.object({ classes: Count, entries: Count })
export const SeriesAnnualProjection = z.object({
  entriesCount: Count,
  entrantsCount: Count,
  averageEntriesPerEntrant: z.number().nonnegative().nullable(),
  clubsCount: Count.nullable(),
  clubsAvailable: z.boolean(),
  coverage: z.object({ assigned: CoveragePart, unresolved: CoveragePart, unmatched: CoveragePart }),
})
export const SeriesEditionAnalytics = z.object({
  offeredClasses: Count,
  activeClasses: Count,
  medianEntriesPerEntrant: z.number().nonnegative().nullable(),
  histogram: z.array(z.object({ label: z.string(), entrants: Count, entries: Count })).length(5),
  concentration: z.array(z.object({ entrants: Count, entries: Count })).max(11),
  topDecile: z.object({ entrants: Count, entries: Count }),
  affiliations: z
    .array(z.object({ label: z.string(), entries: Count }))
    .max(7)
    .nullable(),
})
const TrendClass = z.object({
  id: z.guid(),
  code: z.string(),
  categoryCode: z.string().nullable(),
  entrantsCount: Count,
  originalName: z.string(),
  effectiveName: z.string(),
  division: z.object({ code: z.string(), name: z.string() }),
  count: Count,
})
export const SeriesReportResponse = z.object({
  series: z.object({ id: z.guid(), name: z.string() }),
  fromYear: Year,
  toYear: Year,
  years: z
    .array(
      z.object({
        year: Year,
        edition: z
          .object({
            showId: z.guid(),
            name: z.string(),
            slug: z.string(),
            provisional: z.boolean(),
            canEdit: z.boolean(),
            reviewReasons: z.array(SeriesReviewReason).optional(),
            metrics: SeriesAnnualProjection,
            analytics: SeriesEditionAnalytics,
          })
          .nullable(),
      })
    )
    .max(50),
  trends: z
    .array(
      z.object({
        identity: SeriesTrendIdentity,
        cells: z.array(z.object({ year: Year, class: TrendClass.nullable() })).max(50),
      })
    )
    .max(100),
})
export type SeriesReportOutput = z.infer<typeof SeriesReportResponse>
